import { clsx } from 'clsx';
import { format, parseISO } from 'date-fns';
import React from 'react';
import type { ContactList } from '../../../../../js/api';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSortHeader,
} from '../../../components/table';
import { ContactCard } from './ContactCard';
import { formatContactPhone, formatContactSortName } from './contactDisplay';

const DATE_FORMAT = 'LLL d, yyyy';

export type ContactRow = ContactList & { id: string };

type ContactsResultsProps = {
  contacts: ContactRow[];
  displayType: 'card' | 'table';
  ordering: string;
  onOrderingChange: (next: string) => void;
  showOrganization: boolean;
  selectedId: string | null;
  onOpen: (contactId: string) => void;
  /** Per-row selection checkbox; null when selection is off. */
  renderSelectControl: (contact: ContactRow) => React.ReactNode;
  /** Select-all checkbox for the table header; undefined when selection is off. */
  headerSelectControl?: React.ReactNode;
};

/** The contacts list body: sortable table or card grid, rows open the drawer. */
export const ContactsResults: React.FC<ContactsResultsProps> = ({
  contacts,
  displayType,
  ordering,
  onOrderingChange,
  showOrganization,
  selectedId,
  onOpen,
  renderSelectControl,
  headerSelectControl,
}) => {
  if (displayType === 'card') {
    return (
      <div className="tw:@container">
        <div className="tw:grid tw:grid-cols-1 tw:gap-3 tw:@min-[513px]:grid-cols-2 tw:@min-[793px]:grid-cols-3">
          {contacts.map((contact) => (
            <div
              key={contact.id}
              onClick={() => onOpen(contact.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onOpen(contact.id);
                }
              }}
              role="button"
              tabIndex={0}
            >
              <ContactCard
                contact={contact}
                isSelected={contact.id === selectedId}
                showOrganization={showOrganization}
                selectControl={renderSelectControl(contact)}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const sortHeader = (sortKey: string, label: string) => (
    <TableSortHeader sortKey={sortKey} ordering={ordering} onOrderingChange={onOrderingChange}>
      {label}
    </TableSortHeader>
  );
  const showSelection = headerSelectControl !== undefined;

  return (
    <Table striped dense>
      <TableHead>
        {showSelection && <TableHeader className="tw:w-8">{headerSelectControl}</TableHeader>}
        {sortHeader('last_name', 'Name')}
        {sortHeader('email', 'Email')}
        <TableHeader>Phone</TableHeader>
        {showOrganization && sortHeader('healthcare_entity__name', 'Organization')}
        {sortHeader('facility_count', 'Facilities')}
        {sortHeader('updated_at', 'Last Updated')}
      </TableHead>
      <TableBody>
        {contacts.map((contact) => (
          <TableRow
            key={contact.id}
            onClick={() => onOpen(contact.id)}
            className={clsx(contact.id === selectedId ? 'tw:!bg-brand-green-2' : '')}
            data-testid={`contact-row-${contact.id}`}
          >
            {showSelection && <TableCell>{renderSelectControl(contact)}</TableCell>}
            <TableCell>{formatContactSortName(contact)}</TableCell>
            <TableCell>{contact.email || '--'}</TableCell>
            <TableCell>{formatContactPhone(contact) || '--'}</TableCell>
            {showOrganization && <TableCell>{contact.healthcare_entity_name || '--'}</TableCell>}
            <TableCell>{contact.facility_count ?? 0}</TableCell>
            <TableCell>
              {contact.updated_at ? format(parseISO(contact.updated_at), DATE_FORMAT) : '--'}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
